import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Users, Check, ArrowRight, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import api, { formatApiError } from '@/lib/api';
import { BoostaBolt } from '@/components/BoostaBolt';

export default function AcceptInvite() {
  const [params] = useSearchParams();
  const token = params.get('token');
  const nav = useNavigate();
  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [expired, setExpired] = useState(false);

  useEffect(() => {
    if (!token) { setError('No invitation token provided.'); setLoading(false); return; }
    (async () => {
      try {
        const { data } = await api.get(`/business/invite/${token}`);
        setInvite(data);
        if (data.status === 'active') setAccepted(true);
        else if (data.status === 'expired') { setExpired(true); setError('This invitation has expired. Please ask the workspace owner to resend it.'); }
      } catch (e) {
        setError(formatApiError(e.response?.data?.detail) || 'This invitation is invalid or has expired.');
      } finally { setLoading(false); }
    })();
  }, [token]);

  const accept = async () => {
    setBusy(true);
    try {
      await api.post(`/business/invite/${token}/accept`);
      setAccepted(true);
      toast.success('Invitation accepted!');
    } catch (e) {
      const status = e.response?.status;
      if (status === 410) { setExpired(true); setError('This invitation has expired. Please ask the workspace owner to resend it.'); }
      else toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-boosta-ink flex items-center justify-center px-4 relative overflow-hidden aurora">
      <div className="absolute inset-0 grid-bg opacity-30"/>
      <div className="absolute inset-0 grain"/>
      <div className="absolute top-6 left-6 z-10">
        <Link to="/" className="flex items-center gap-2"><BoostaBolt size={28} animate/><span className="font-display font-bold text-3d">Boosta</span></Link>
      </div>
      <div className="relative w-full max-w-md z-10">
        <div className="glass rounded-3xl p-8 md:p-10" data-testid="accept-invite-root">
          {loading ? (
            <div className="text-center text-white/55 text-sm py-8" data-testid="accept-invite-loading">Loading invitation…</div>
          ) : error ? (
            <div className="text-center" data-testid="accept-invite-error">
              <div className="w-14 h-14 rounded-2xl bg-red-500/10 flex items-center justify-center mx-auto mb-4"><AlertCircle className="w-6 h-6 text-red-400"/></div>
              <h1 className="font-display font-black text-2xl text-3d">{expired ? 'Invitation expired' : 'Invitation not found'}</h1>
              <p className="text-white/55 text-sm mt-2">{error}</p>
              <Link to="/" className="mt-6 inline-flex items-center gap-2 text-sm text-boosta-cyan hover:underline">Go home</Link>
            </div>
          ) : accepted ? (
            <div className="text-center" data-testid="accept-invite-success">
              <div className="w-14 h-14 rounded-2xl bg-boosta-emerald/10 flex items-center justify-center mx-auto mb-4"><Check className="w-6 h-6 text-boosta-emerald"/></div>
              <h1 className="font-display font-black text-2xl text-3d">You're in! 🎉</h1>
              <p className="text-white/55 text-sm mt-2">You've joined <span className="text-boosta-yellow font-bold">{invite?.workspace}</span> as <span className="text-boosta-cyan capitalize">{invite?.role}</span>. Create your account to get started.</p>
              <button onClick={()=>nav('/register')} className="mt-6 w-full btn-primary rounded-xl py-3.5 font-bold inline-flex items-center justify-center gap-2" data-testid="accept-invite-signup">
                Create your account <ArrowRight className="w-4 h-4"/>
              </button>
              <Link to="/login" className="mt-4 inline-block text-sm text-white/45 hover:text-boosta-cyan">Already have an account? Sign in</Link>
            </div>
          ) : (
            <div className="text-center" data-testid="accept-invite-card">
              <div className="w-14 h-14 rounded-2xl bg-boosta-cyan/10 flex items-center justify-center mx-auto mb-4"><Users className="w-6 h-6 text-boosta-cyan"/></div>
              <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-boosta-cyan mb-1">Team invitation</div>
              <h1 className="font-display font-black text-2xl text-3d">Join {invite?.workspace}</h1>
              <p className="text-white/55 text-sm mt-2">
                <span className="text-white font-medium">{invite?.inviter || 'A teammate'}</span> invited you to join their Boosta workspace as <span className="text-boosta-cyan capitalize font-bold">{invite?.role}</span>.
              </p>
              <div className="mt-3 text-xs text-white/40">{invite?.email}</div>
              <button onClick={accept} disabled={busy} className="mt-7 w-full btn-primary rounded-xl py-3.5 font-bold inline-flex items-center justify-center gap-2 disabled:opacity-50" data-testid="accept-invite-btn">
                {busy ? 'Accepting…' : <>Accept invitation <Check className="w-4 h-4"/></>}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
